import { authApiClient } from "./authApiClient";
import {
  IActivateAccount,
  IRegister,
  LoginResponse,
  RegisterResponse,
} from "../modules/auth/model/auth";
import { throwError } from "../utils/errorHandling";

// Utility to manage tokens securely
const TOKEN_KEY = "access_token" as const;
const REFRESH_TOKEN_KEY = "refresh_token" as const;

export const tokenStorage = {
  setTokens: ({ access_token, refresh_token }: LoginResponse["data"]): void => {
    const storage = localStorage || sessionStorage;
    storage.setItem(TOKEN_KEY, access_token);
    if (refresh_token) {
      storage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }
  },
  getAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  },
  getRefreshToken: (): string | null => {
    return (
      localStorage.getItem(REFRESH_TOKEN_KEY) ||
      sessionStorage.getItem(REFRESH_TOKEN_KEY)
    );
  },
  clearTokens: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// Function to handle login
export const signIn = async (
  login: string,
  password: string
): Promise<LoginResponse["data"]> => {
  try {
    const response = await authApiClient.post<LoginResponse>(`/auth/login`, {
      login,
      password,
    });
    //

    const { data } = response.data;
    if (!data?.access_token) {
      throw new Error("Invalid login response: No access token received");
    }
    tokenStorage.setTokens(data);
    const params = new URLSearchParams(window.location.search)
    const redirect = params.get('redirect');

    if (redirect) {
      window.location.href = decodeURIComponent(redirect);
    }

    return {
      ...data,
      user_profile: data.user_profile || [], // Ensure this matches your API response
    };
  } catch (error: unknown) {
    throw new Error(throwError(error, "Login failed. Please try again."));
  }
};

export const activateAccount = async ({
  password,
  password_confirmation,
  token
}: IActivateAccount) => {
  try {
    const response = await authApiClient.post<RegisterResponse>(
      "/auth/activate_account",
      {
        password,
        token
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw new Error(throwError(error, "Activation failed"));
  }
};

// Registration function (updated to use login instead of email)
export const register = async ({
  email,
  password,
  matricule,
  firstName,
  lastName,
  jobTitle,
  phone,
}: IRegister): Promise<RegisterResponse> => {
  try {
    const response = await authApiClient.post<RegisterResponse>(
      "/auth/register",
      {
        email,
        password,
        matricule,
        firstName,
        lastName,
        jobTitle,
        phone,
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw new Error(throwError(error, "Registration failed"));
  }
};
